import React, { useState, useEffect, useMemo } from 'react';
import { PresentationTemplate, ProjectInfo, Task, PhysicalUnit, Level, Responsible, CNCGroup, ProjectProgressSnapshot } from '../../types';
import PresentationEditor from '../presentations/PresentationEditor';
import { useSyncState } from '../../hooks/useSyncState';
import { WeeklyPlanDataProps } from '../presentations/WeeklyPlanTableWidget';
import {
    fetchMasterTemplates,
    saveMasterTemplate,
    deleteMasterTemplate,
    resetMasterTemplatesToDefault,
} from '../../services/presentationService';

interface PresentationTemplateManagerProps {
    projects?: (ProjectInfo & { id: string })[];
    currentUserName?: string;
}

const CATEGORIES = [
    { id: 'todas', label: 'Todas las Categorías', icon: 'apps' },
    { id: 'semanal', label: 'Planificación Semanal', icon: 'view_week' },
    { id: 'intermedia', label: 'Lookahead / Intermedia', icon: 'view_timeline' },
    { id: 'gerencial', label: 'Control Gerencial', icon: 'monitoring' },
    { id: 'calidad', label: 'Calidad & SC', icon: 'fact_check' },
    { id: 'general', label: 'General / Personalizadas', icon: 'dashboard_customize' },
];

export const PresentationTemplateManager: React.FC<PresentationTemplateManagerProps> = ({
    projects = [],
    currentUserName = 'Administrador',
}) => {
    const [templates, setTemplates] = useState<PresentationTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string>('todas');
    const [searchQuery, setSearchQuery] = useState('');
    const [notification, setNotification] = useState<string | null>(null);

    // Editing in PresentationEditor
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [templateToEdit, setTemplateToEdit] = useState<PresentationTemplate | null>(null);

    // Load templates
    const loadTemplates = async () => {
        setIsLoading(true);
        try {
            const data = await fetchMasterTemplates();
            setTemplates(data);
        } catch (error) {
            console.error('Error fetching master presentation templates:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    const showNotify = (msg: string) => {
        setNotification(msg);
        setTimeout(() => setNotification(null), 3000);
    };

    // Filtered templates
    const filteredTemplates = useMemo(() => {
        return templates.filter(tmpl => {
            const matchCategory = selectedCategory === 'todas' || (tmpl.category || 'general') === selectedCategory;
            const matchSearch =
                (tmpl.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (tmpl.description || '').toLowerCase().includes(searchQuery.toLowerCase());
            return matchCategory && matchSearch;
        });
    }, [templates, selectedCategory, searchQuery]);

    // Handle create new
    const handleCreateNew = () => {
        const newTemplate: PresentationTemplate = {
            id: `master-tpl-${Date.now()}`,
            projectId: 'global',
            name: 'Nueva Plantilla Maestra',
            description: 'Plantilla base estándar corporativa para reuniones de obra.',
            category: selectedCategory !== 'todas' ? (selectedCategory as any) : 'semanal',
            isDefault: false,
            createdBy: currentUserName,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            slides: [
                {
                    id: `slide-${Date.now()}`,
                    order: 0,
                    background: '#ffffff',
                    widgets: [],
                },
            ],
        };
        setTemplateToEdit(newTemplate);
        setIsEditorOpen(true);
    };

    // Handle edit existing
    const handleEdit = (tmpl: PresentationTemplate) => {
        setTemplateToEdit(JSON.parse(JSON.stringify(tmpl)));
        setIsEditorOpen(true);
    };

    // Handle duplicate
    const handleDuplicate = async (tmpl: PresentationTemplate) => {
        const copy: PresentationTemplate = {
            ...JSON.parse(JSON.stringify(tmpl)),
            id: `master-tpl-${Date.now()}`,
            name: `${tmpl.name} (Copia)`,
            isDefault: false,
            createdBy: currentUserName,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        await saveMasterTemplate(copy);
        await loadTemplates();
        showNotify(`Plantilla "${tmpl.name}" duplicada con éxito.`);
    };

    // Handle delete
    const handleDelete = async (id: string, name: string) => {
        if (!confirm(`¿Eliminar la plantilla maestra "${name}"?`)) return;
        await deleteMasterTemplate(id);
        await loadTemplates();
        showNotify(`Plantilla "${name}" eliminada.`);
    };

    // Handle restore defaults
    const handleRestoreDefaults = async () => {
        if (!confirm('¿Deseas restaurar las plantillas maestras estándar predeterminadas (Semanal, Intermedia, Gerencial y Calidad)? Se mantendrán también las que hayas creado.')) return;
        resetMasterTemplatesToDefault();
        await loadTemplates();
        showNotify('Plantillas corporativas estándar restauradas.');
    };

    // Callback when saving from PresentationEditor
    const handleSaveMasterCallback = async (saved: PresentationTemplate) => {
        await loadTemplates();
        showNotify(`Plantilla maestra "${saved.name}" guardada.`);
    };

    // Category badge helpers
    const getCategoryBadge = (cat?: string) => {
        switch (cat) {
            case 'semanal':
                return { label: 'Semanal', bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' };
            case 'intermedia':
                return { label: 'Lookahead', bg: 'bg-indigo-50 dark:bg-indigo-950/40', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-800' };
            case 'gerencial':
                return { label: 'Gerencial', bg: 'bg-purple-50 dark:bg-purple-950/40', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' };
            case 'calidad':
                return { label: 'Calidad', bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' };
            default:
                return { label: 'General', bg: 'bg-slate-50 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700' };
        }
    };

    // Determinar obra referencial para el diseñador de plantillas maestras ("318 - 94 Viviendas Las Quilas")
    const referentialProject = useMemo(() => {
        if (!projects || projects.length === 0) return null;
        return projects.find(p => {
            const name = (p.name || '').toLowerCase();
            const code = (p.code || '').toLowerCase();
            const id = String(p.id || '').toLowerCase();
            return name.includes('las quilas') || name.includes('318') || code.includes('318') || id.includes('318');
        }) || projects[0] || null;
    }, [projects]);

    const refCollection = referentialProject ? `project_${referentialProject.id}` : 'data';

    // Carga de datos de la obra referencial para alimentar los widgets interactivos del diseñador maestro
    const [refTasks] = useSyncState<Task[]>(refCollection, 'tasks', []);
    const [refUnits] = useSyncState<PhysicalUnit[]>(refCollection, 'units', []);
    const [refLevels] = useSyncState<Level[]>(refCollection, 'levels', []);
    const [refMatrixData] = useSyncState<Record<string, any>>(refCollection, 'matrixData', {});
    const [refProtocolConfigs] = useSyncState<Record<string, any>>(refCollection, 'protocolConfigs', {});
    const [refSavedProtocols] = useSyncState<Record<string, any>>(refCollection, 'savedProtocols', {});
    const [refWeeklyPlans] = useSyncState<any[]>(refCollection, 'weeklyPlans', []);
    const [refResponsibles] = useSyncState<Responsible[]>(refCollection, 'responsibles', []);
    const [refSubcontractors] = useSyncState<any[]>(refCollection, 'subcontractors', []);
    const [refContracts] = useSyncState<any[]>(refCollection, 'contracts', []);
    const [refBaselines] = useSyncState<any[]>(refCollection, 'baselines', []);
    const [refProgressHistory] = useSyncState<ProjectProgressSnapshot[]>(refCollection, 'progressHistory', []);
    const [refEvaluationConfig] = useSyncState<any>(refCollection, 'evaluationConfig', null);
    const [refLookaheadPlans] = useSyncState<any[]>(refCollection, 'lookaheadPlans', []);
    const [globalCNCGroups] = useSyncState<CNCGroup[]>('data', 'globalCNCGroups', []);
    const [localCNCGroups] = useSyncState<CNCGroup[]>(refCollection, 'cncGroups', []);
    const [globalUsers] = useSyncState<any[]>('data', 'globalUsers', []);

    // Ensamblar objeto WeeklyPlanDataProps referencial
    const weeklyPlanData: WeeklyPlanDataProps = useMemo(() => {
        const sortedPlans = [...refWeeklyPlans].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
        const activePlan = sortedPlans.find(p => p.status !== 'Cerrada') || sortedPlans[sortedPlans.length - 1] || null;

        return {
            weeklyPlans: refWeeklyPlans,
            tasks: refTasks,
            units: refUnits,
            levels: refLevels,
            responsibles: refResponsibles,
            cncGroups: (localCNCGroups && localCNCGroups.length > 0) ? localCNCGroups : globalCNCGroups,
            savedProtocols: refSavedProtocols,
            protocolConfigs: refProtocolConfigs,
            activePlanId: activePlan?.id || null,
            matrixData: refMatrixData,
            projectInfo: referentialProject || undefined,
            subcontractors: refSubcontractors,
            contracts: refContracts,
            baselines: refBaselines,
            progressHistory: refProgressHistory,
            evaluationConfig: refEvaluationConfig,
            lookaheadPlans: refLookaheadPlans,
            globalUsers: globalUsers,
        };
    }, [
        refWeeklyPlans,
        refTasks,
        refUnits,
        refLevels,
        refResponsibles,
        localCNCGroups,
        globalCNCGroups,
        refSavedProtocols,
        refProtocolConfigs,
        refMatrixData,
        referentialProject,
        refSubcontractors,
        refContracts,
        refBaselines,
        refProgressHistory,
        refEvaluationConfig,
        refLookaheadPlans,
        globalUsers,
    ]);

    // Project info for master editor mode (uses referential project info if found, or fallback)
    const editorProjectInfo: ProjectInfo = useMemo(() => {
        if (referentialProject) {
            return {
                ...referentialProject,
                name: `${referentialProject.name} (Plantilla Maestra Corporativa)`,
            };
        }
        return {
            id: 'global',
            name: 'Plantillas Maestras Corporativas',
            code: 'MASTER',
            startDate: '',
            endDate: '',
            status: 'Active',
        };
    }, [referentialProject]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-5">
                <div>
                    <div className="flex items-center gap-2.5">
                        <div className="size-10 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold">
                            <span className="material-symbols-outlined text-[24px]">slideshow</span>
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                                Maestro de Presentaciones
                            </h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                Plantillas estándar corporativas 16:9 para reuniones de Planificación Semanal, Lookahead, Control Gerencial y Calidad.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    {notification && (
                        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-xl border border-emerald-200 dark:border-emerald-800 animate-in fade-in">
                            <span className="material-symbols-outlined text-[16px]">check_circle</span>
                            {notification}
                        </span>
                    )}

                    <button
                        onClick={handleRestoreDefaults}
                        className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer"
                        title="Restablecer plantillas maestras predeterminadas"
                    >
                        <span className="material-symbols-outlined text-[18px]">restart_alt</span>
                        Restaurar Estándares
                    </button>

                    <button
                        onClick={handleCreateNew}
                        className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-600/20 transition-all active:scale-95 cursor-pointer"
                    >
                        <span className="material-symbols-outlined text-[18px]">add_circle</span>
                        + Nueva Plantilla
                    </button>
                </div>
            </div>

            {/* Filter Tabs & Search Bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50 dark:bg-slate-850 p-3 rounded-2xl border border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                                selectedCategory === cat.id
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                            }`}
                        >
                            <span className="material-symbols-outlined text-[16px]">{cat.icon}</span>
                            <span>{cat.label}</span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-64">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                    <input
                        type="text"
                        placeholder="Buscar plantilla maestra..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                </div>
            </div>

            {/* Content: Templates Grid */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
                    <span className="material-symbols-outlined animate-spin text-[36px]">progress_activity</span>
                    <span className="text-xs font-semibold">Cargando plantillas maestras...</span>
                </div>
            ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 dark:bg-slate-850/50 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8">
                    <div className="size-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-500 flex items-center justify-center mx-auto mb-3">
                        <span className="material-symbols-outlined text-[32px]">slideshow</span>
                    </div>
                    <h3 className="text-sm font-bold text-slate-800 dark:text-white">No se encontraron plantillas maestras</h3>
                    <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                        {searchQuery ? 'Prueba con otro término de búsqueda o categoría.' : 'Comienza creando una nueva plantilla o restaurando las predeterminadas.'}
                    </p>
                    <div className="mt-4 flex items-center justify-center gap-2">
                        <button
                            onClick={handleRestoreDefaults}
                            className="px-3 py-1.5 text-xs font-semibold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50"
                        >
                            Restaurar Estándares
                        </button>
                        <button
                            onClick={handleCreateNew}
                            className="px-3.5 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                        >
                            + Nueva Plantilla
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                    {filteredTemplates.map(tmpl => {
                        const badge = getCategoryBadge(tmpl.category);
                        const slideCount = tmpl.slides ? tmpl.slides.length : 0;
                        const widgetCount = tmpl.slides ? tmpl.slides.reduce((acc, s) => acc + (s.widgets?.length || 0), 0) : 0;

                        return (
                            <div
                                key={tmpl.id}
                                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 flex flex-col justify-between gap-4 shadow-sm hover:shadow-lg hover:border-indigo-500/50 transition-all group"
                            >
                                <div className="space-y-3">
                                    {/* Top Row: Icon, Title & Badge */}
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2.5">
                                            <div className="size-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold flex-shrink-0">
                                                <span className="material-symbols-outlined text-[22px]">
                                                    {tmpl.category === 'semanal'
                                                        ? 'view_week'
                                                        : tmpl.category === 'intermedia'
                                                        ? 'view_timeline'
                                                        : tmpl.category === 'gerencial'
                                                        ? 'monitoring'
                                                        : tmpl.category === 'calidad'
                                                        ? 'fact_check'
                                                        : 'slideshow'}
                                                </span>
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="font-extrabold text-sm text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-1">
                                                    {tmpl.name}
                                                </h3>
                                                <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                                                    <span>{slideCount} diapositiva{slideCount !== 1 ? 's' : ''}</span>
                                                    <span>•</span>
                                                    <span>{widgetCount} elemento{widgetCount !== 1 ? 's' : ''}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg border ${badge.bg} ${badge.text} ${badge.border} flex-shrink-0`}>
                                            {badge.label}
                                        </span>
                                    </div>

                                    {/* Description */}
                                    <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed min-h-[32px]">
                                        {tmpl.description || 'Plantilla de presentación base para reuniones técnicas y de control.'}
                                    </p>

                                    {/* Mini Preview Deck (Representation of slides) */}
                                    <div className="flex items-center gap-1.5 py-1 overflow-x-auto">
                                        {(tmpl.slides || []).slice(0, 5).map((slide, sIdx) => (
                                            <div
                                                key={slide.id || sIdx}
                                                className="w-12 h-7 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-center text-[9px] font-bold text-slate-400 dark:text-slate-500 flex-shrink-0 shadow-xs"
                                                style={{ backgroundColor: slide.background || '#ffffff' }}
                                                title={`Diapositiva ${sIdx + 1} (${slide.widgets?.length || 0} widgets)`}
                                            >
                                                {sIdx + 1}
                                            </div>
                                        ))}
                                        {(tmpl.slides || []).length > 5 && (
                                            <div className="text-[10px] font-bold text-slate-400 px-1">
                                                +{(tmpl.slides || []).length - 5}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer & Actions */}
                                <div className="border-t border-slate-100 dark:border-slate-800 pt-3 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1 text-[11px] text-slate-400 truncate">
                                        <span className="material-symbols-outlined text-[14px]">person</span>
                                        <span className="truncate">{tmpl.createdBy || 'Sistema'}</span>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                        <button
                                            type="button"
                                            onClick={() => handleDuplicate(tmpl)}
                                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-lg transition-colors cursor-pointer"
                                            title="Duplicar plantilla"
                                        >
                                            <span className="material-symbols-outlined text-[17px]">content_copy</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleEdit(tmpl)}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/60 dark:text-indigo-400 text-xs font-bold rounded-xl transition-all cursor-pointer"
                                            title="Editar presentación en diseñador WYSIWYG"
                                        >
                                            <span className="material-symbols-outlined text-[15px]">design_services</span>
                                            <span>Editar</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleDelete(tmpl.id, tmpl.name)}
                                            className="flex items-center gap-1 px-2.5 py-1.5 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-xs font-semibold rounded-xl border border-transparent hover:border-rose-200 dark:hover:border-rose-900/50 transition-all cursor-pointer"
                                            title="Eliminar presentación"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">delete</span>
                                            <span>Eliminar</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* WYSIWYG 16:9 Presentation Editor Modal */}
            {isEditorOpen && (
                <PresentationEditor
                    projectInfo={editorProjectInfo}
                    weeklyPlanData={weeklyPlanData}
                    currentUserName={currentUserName}
                    initialTemplate={templateToEdit || undefined}
                    isMasterMode={true}
                    onSaveMaster={handleSaveMasterCallback}
                    onClose={() => {
                        setIsEditorOpen(false);
                        setTemplateToEdit(null);
                        loadTemplates();
                    }}
                />
            )}
        </div>
    );
};

export default PresentationTemplateManager;
